"use client";
import * as React from "react";
import { createContext, useState, useEffect } from "react";
import EditorMenu from "./EditorMenu";
// Create the context with a default value
export const EditablePageContext = createContext({
    selectedWidget: null,
    setSelectedWidget: () => { },
    handleMoveUp: () => { },
    handleMoveDown: () => { },
    handleDeleteBlock: () => { },
    blockOrder: [],
    deletedBlockIds: [],
});
export default function EditablePage({ config, children }) {
    // Create state for the selected widget
    const [selectedWidget, setSelectedWidget] = useState(null);
    // Create state for the block order
    const [blockOrder, setBlockOrder] = useState([]);
    // Create state for deleted blocks
    const [deletedBlockIds, setDeletedBlockIds] = useState([]);
    // Initialize the block order when the component mounts or children change
    useEffect(() => {
        var _a;
        if (children && children.length > 0) {
            // Extract unique IDs from children
            const ids = ((_a = React.Children.map(children, (child) => {
                var _a, _b;
                if (React.isValidElement(child) &&
                    ((_b = (_a = child.props) === null || _a === void 0 ? void 0 : _a.block) === null || _b === void 0 ? void 0 : _b.uniqueId)) {
                    return child.props.block.uniqueId;
                }
                return null;
            })) === null || _a === void 0 ? void 0 : _a.filter(Boolean)) || [];
            setBlockOrder(ids);
        }
    }, [children]);
    // Function to move a block up in the order
    const handleMoveUp = (blockId) => {
        setBlockOrder((prevOrder) => {
            const index = prevOrder.indexOf(blockId);
            if (index <= 0)
                return prevOrder; // Can't move up if it's the first item
            const newOrder = [...prevOrder];
            // Swap with the previous element
            [newOrder[index - 1], newOrder[index]] = [
                newOrder[index],
                newOrder[index - 1],
            ];
            return newOrder;
        });
    };
    // Function to move a block down in the order
    const handleMoveDown = (blockId) => {
        setBlockOrder((prevOrder) => {
            const index = prevOrder.indexOf(blockId);
            if (index === -1 || index >= prevOrder.length - 1)
                return prevOrder; // Can't move down if it's the last item
            const newOrder = [...prevOrder];
            // Swap with the next element
            [newOrder[index], newOrder[index + 1]] = [
                newOrder[index + 1],
                newOrder[index],
            ];
            return newOrder;
        });
    };
    // Function to delete a block
    const handleDeleteBlock = (blockId) => {
        // Add the block ID to the deletedBlockIds array
        setDeletedBlockIds(prevIds => [...prevIds, blockId]);
        // Remove from the block order as well
        setBlockOrder(prevOrder => prevOrder.filter(id => id !== blockId));
        // If the deleted block contains the selected widget, clear the selection
        if (selectedWidget) {
            setSelectedWidget(null);
        }
    };
    // Create the context value
    const contextValue = React.useMemo(() => ({
        selectedWidget,
        setSelectedWidget,
        handleMoveUp,
        handleMoveDown,
        handleDeleteBlock,
        blockOrder,
        deletedBlockIds,
    }), [selectedWidget, blockOrder, deletedBlockIds]);
    // Sort children according to blockOrder before rendering
    const sortedChildren = React.useMemo(() => {
        if (!children || children.length === 0 || blockOrder.length === 0) {
            return children;
        }
        // Create a map of blockId to child component
        const childMap = new Map();
        React.Children.forEach(children, (child) => {
            var _a, _b;
            if (React.isValidElement(child) &&
                ((_b = (_a = child.props) === null || _a === void 0 ? void 0 : _a.block) === null || _b === void 0 ? void 0 : _b.uniqueId) &&
                !deletedBlockIds.includes(child.props.block.uniqueId)) {
                childMap.set(child.props.block.uniqueId, child);
            }
        });
        // Return the children in the order specified by blockOrder,
        // filtering out any deleted blocks
        return blockOrder
            .filter(id => !deletedBlockIds.includes(id))
            .map((id) => childMap.get(id))
            .filter(Boolean);
    }, [children, blockOrder, deletedBlockIds]);
    return (React.createElement(EditablePageContext.Provider, { value: contextValue },
        React.createElement(EditorMenu, null),
        sortedChildren));
}
